import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
    sendMeetingRequest,
    validateEmail,
    validatePhone,
    validateRequired,
    rateLimiter,
    handleApiError,
    getSuccessMessage
} from '../../utils/api';
import { MEETING_CONFIG } from '../../config/environment';
import './style.css';

const MeetingScheduler = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        date: '',
        time: '',
        message: '',
        propertyInterest: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Prevent body scroll when popup is open
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('popup-open');
        } else {
            document.body.classList.remove('popup-open');
        }

        // Cleanup on unmount
        return () => {
            document.body.classList.remove('popup-open');
        };
    }, [isOpen]);

    const timeSlots = MEETING_CONFIG.TIME_SLOTS;
    const propertyOptions = MEETING_CONFIG.PROPERTY_OPTIONS;

    const validateForm = () => {
        const errors = [];

        if (!validateRequired(formData.name)) {
            errors.push('Name is required');
        }

        if (!validateEmail(formData.email)) {
            errors.push('Valid email is required');
        }

        if (!validatePhone(formData.phone)) {
            errors.push('Valid phone number is required');
        }

        if (!validateRequired(formData.date)) {
            errors.push('Date is required');
        }

        if (!validateRequired(formData.time)) {
            errors.push('Time is required');
        }

        return errors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate form
        const errors = validateForm();
        if (errors.length > 0) {
            toast.error('Please fix the following errors:\n' + errors.join('\n'));
            return;
        }

        // Check rate limiting
        if (!rateLimiter.canMakeRequest('/api/send-meeting-request')) {
            toast.error('Too many requests. Please wait a moment and try again.');
            return;
        }

        setIsSubmitting(true);

        try {
            // Send meeting request via API
            const response = await sendMeetingRequest(formData);

            if (response.success) {
                // Reset form
                setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    date: '',
                    time: '',
                    message: '',
                    propertyInterest: ''
                });

                // Show success toast
                toast.success(getSuccessMessage('meeting'));

                onClose();
            } else {
                throw new Error(response.message || 'Failed to send meeting request');
            }

        } catch (error) {
            const errorMessage = handleApiError(error, 'meeting');
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // Get minimum and maximum dates from configuration
    const today = new Date();
    const minDate = new Date(today.getTime() + (MEETING_CONFIG.MIN_DATE_OFFSET * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];
    const maxDate = new Date(today.getTime() + (MEETING_CONFIG.MAX_DATE_OFFSET * 24 * 60 * 60 * 1000)).toISOString().split('T')[0];

    if (!isOpen) return null;

    return (
        <div className="meeting-scheduler-overlay" onClick={onClose}>
            <div className="meeting-scheduler-modal" onClick={(e) => e.stopPropagation()}>
                <div className="meeting-scheduler-header">
                    <h3>Schedule a Meeting</h3>
                    <button className="close-btn" onClick={onClose}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="meeting-form">
                    <div className="form-group">
                        <input
                            type="text"
                            name="name"
                            placeholder="Your Full Name *"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="form-group">
                        <input
                            type="email"
                            name="email"
                            placeholder="Your Email *"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="form-group">
                        <input
                            type="tel"
                            name="phone"
                            placeholder="Your Phone Number *"
                            value={formData.phone}
                            onChange={handleInputChange}
                            required
                            disabled={isSubmitting}
                        />
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>Preferred Date *</label>
                            <input
                                type="date"
                                name="date"
                                value={formData.date}
                                onChange={handleInputChange}
                                min={minDate}
                                max={maxDate}
                                required
                                disabled={isSubmitting}
                            />
                        </div>

                        <div className="form-group">
                            <label>Preferred Time *</label>
                            <select
                                name="time"
                                value={formData.time}
                                onChange={handleInputChange}
                                required
                                disabled={isSubmitting}
                            >
                                <option value="">Select Time</option>
                                {timeSlots.map((time, index) => (
                                    <option key={index} value={time}>{time}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>Property Interest</label>
                        <select
                            name="propertyInterest"
                            value={formData.propertyInterest}
                            onChange={handleInputChange}
                            disabled={isSubmitting}
                        >
                            <option value="">Select Property</option>
                            {propertyOptions.map((property, index) => (
                                <option key={index} value={property}>{property}</option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <textarea
                            name="message"
                            placeholder="Additional Message (Optional)"
                            value={formData.message}
                            onChange={handleInputChange}
                            rows="4"
                            disabled={isSubmitting}
                        />
                    </div>

                    <button
                        type="submit"
                        className="submit-btn"
                        disabled={isSubmitting}
                        style={{
                            opacity: isSubmitting ? 0.7 : 1,
                            cursor: isSubmitting ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {isSubmitting ? (
                            <>
                                <div className="spinner" style={{
                                    width: '16px',
                                    height: '16px',
                                    border: '2px solid transparent',
                                    borderTop: '2px solid white',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite',
                                    marginRight: '8px'
                                }}></div>
                                Sending...
                            </>
                        ) : (
                            <>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                                </svg>
                                Schedule Meeting
                            </>
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default MeetingScheduler; 